import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import {
  PhaseSheet,
  type PhaseSheetMode,
  type PhaseSheetProps,
  type SheetSelectOption,
} from './PhaseSheet';
import { PhaseAddDialog, type PhaseAddDialogProps, type PhaseDialogMode } from './PhaseAddDialog';
import { canExportStageResponses } from './responses-matrix';
import {
  DownloadIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  EyeOffIcon,
  InfoIcon,
  LockIcon,
  PlusIcon,
  TrashIcon,
} from './icons';
import { StageReopenDialog } from './StageReopenDialog';
import type { ResubmitLock } from './resubmit-lock';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';
import { JobConfirmDialog } from '@/features/jobs/jobs-list/JobConfirmDialog';

export type StageBadgeType =
  | 'conteudo'
  | 'form'
  | 'fator-h'
  | 'case'
  | 'teste'
  | 'teste-externo'
  | 'curriculo'
  | 'complementacao-perfil';

/**
 * Phase-level type (EU-165 RN-04). Closed enum used internally as a funnel
 * analytics discriminator — the candidate sees the editable `name` instead.
 * `null` covers legacy rows that pre-date EU-165; the consultor picks a value
 * on the next edit.
 */
// RN 2026-07-30 — 5 types oficiais (Trilha online, Jornada de aprofundamento,
// Dinâmica, Entrevista). `contratacao-final` mantido no union pra leitura
// de vagas legadas (write-tolerance no BFF), mas NÃO oferecido no
// picker — quem tinha fase Contratação Final continua editando, mas não
// dá pra criar nova com esse type.
export type PhaseTypeBadge =
  | 'trilha-online'
  | 'jornada-aprofundamento'
  | 'dinamica'
  | 'entrevista'
  | 'contratacao-final';

export const PHASE_TYPE_OPTIONS: readonly { value: PhaseTypeBadge; label: string }[] = [
  { value: 'trilha-online', label: 'Trilha Online' },
  { value: 'jornada-aprofundamento', label: 'Jornada de Aprofundamento' },
  { value: 'dinamica', label: 'Dinâmica' },
  { value: 'entrevista', label: 'Entrevista' },
  // RN Restrições de fase inicial/final (2026-07-30) — Contratação voltou ao
  // picker: é o teto do funil (a partir dela não se avança candidato).
  { value: 'contratacao-final', label: 'Contratação' },
];

export interface JobStage {
  id: string;
  name: string;
  type: StageBadgeType;
  /** ISO `YYYY-MM-DD` or null when the stage has no scheduled start. */
  startDate: string | null;
  /** ISO `YYYY-MM-DD` or null when the stage has no scheduled end. */
  endDate: string | null;
  /** `HH:MM` (24h) or null. Optional even when dates are set. */
  endTime: string | null;
  hidden?: boolean;
  /**
   * EU-764 RN-16 — o instrumento desta etapa não se aplica à vaga (Mapa de
   * Decisões em vaga de aprendiz). Enquanto a regra valer, reexibir é
   * proibido. Vem derivado do Core, não gravado.
   */
  hiddenLocked?: boolean;
  /**
   * Catalogue references picked in the stage editor sheet. Round-trip
   * through the BFF so the operator's selection survives a page reload.
   * Each is `null` when the stage type doesn't use that catalogue (e.g.
   * a 'conteudo' stage has all three null).
   */
  formId?: string | null;
  testId?: string | null;
  fatorHId?: string | null;
  /**
   * `'sim' | 'nao' | null` — operator-picked answer to "será possível
   * reenviar a resposta?". Stored verbatim and re-seeded into the sheet
   * draft when the stage is reopened for editing.
   */
  canResubmit?: 'sim' | 'nao' | null;
  /**
   * Conteúdo textual da etapa do tipo `conteudo` (EU-165 Juliana TODO #4a).
   * Round-trip pelo BFF/Core; round-trip pelo modal de edit para que o consultor
   * veja o valor salvo na hidratação.
   */
  contentText?: string | null;
  /**
   * Nome do arquivo enviado para etapas do tipo `conteudo` (EU-165 Juliana
   * TODO #4b). Round-trip pelo BFF/Core; exibido no FileInput em mode edit.
   */
  contentFileName?: string | null;
  /**
   * URL pública (S3) da mídia da etapa `conteudo`, retornada pelo upload
   * (`POST /jobs/:id/content-asset`). Round-trip via `module.config.fileUrl` —
   * é o que o candidato consome para renderizar vídeo/PDF.
   */
  contentFileUrl?: string | null;
  /** Tipo da mídia (`pdf`/`video`) — derivado do MIME no upload. */
  contentFileType?: 'pdf' | 'video' | null;
  /**
   * Etapa de teste externo (e Fator H): modo de exibição (`embed`/`nova-aba`)
   * e URL da plataforma. Round-trip via `module.config` (`embedMode`+`externalUrl`).
   */
  externalEmbedMode?: 'embed' | 'nova-aba' | 'redirect' | null;
  externalUrl?: string | null;
  /**
   * EU-539 — plataforma da etapa `teste-externo` (`'url'` genérica vs
   * `'kolab'`) + instância Kolab escolhida. Round-trip via `module.config`
   * (`platform` + `kolabInstanceId`).
   */
  externalPlatform?: 'url' | 'kolab' | null;
  kolabInstanceId?: string | null;
  /**
   * `module.config` JSONB cru, preservado verbatim para round-trip fiel ao
   * Core. O editor só reescreve a config das etapas cujo config ele de fato
   * autora (`conteudo`, `teste-externo`, `fator-h` — reconstruídos a partir
   * dos campos do formulário); para os demais tipos (ex.: `complementacao-perfil`,
   * que guarda `{ requiredFields }`) a config crua é re-enviada sem alteração.
   * Sem isso o write mandava `config: null`, o Core via a config "mudar" a cada
   * save e disparava `module_reopen_decision_required` em etapas concluídas que
   * o consultor nem tocou (bug do modal de reabertura que não aparecia).
   */
  rawConfig?: Record<string, unknown> | null;
  /**
   * RN-06 — número de candidatos que já concluíram esta etapa. Quando `> 0`,
   * salvar uma edição da etapa abre o modal "Reabrir etapa" (RN-07).
   */
  completedCount?: number;
  /**
   * RN-10 — número de candidatos que já responderam esta etapa (incluindo
   * incompletas). Quando `> 0` o consultor não pode excluir a etapa, só
   * ocultar (`handleStageAction('delete')` redireciona para o fluxo de
   * hide com toast explicativo).
   */
  answerCount?: number;
}

export interface JobPhase {
  id: string;
  index: number;
  name: string;
  /** EU-165 RN-04 — funnel-analytics discriminator. Null on legacy rows. */
  type: PhaseTypeBadge | null;
  /** ISO `YYYY-MM-DD` or null. Exibido como "Começa: DD/MM/YY" no card. */
  startDate: string | null;
  /** ISO `YYYY-MM-DD` or null. Exibido como "Encerra: DD/MM/YY às HH:MM". */
  endDate: string | null;
  /** `HH:MM` (24h) ou null. Default `'23:59'` aplicado pelo repositório do Core. */
  endTime: string | null;
  stages: JobStage[];
  /**
   * FAS RN-06 — número de candidatos inscritos na fase (qualquer atividade:
   * `job_step_user_progress` ∪ `module_user_response` via module, DISTINCT
   * user_id). Quando `> 0`, o handler de delete da fase aborta inline com
   * um toast — o modal de confirmação nem chega a abrir.
   */
  enrolledCount?: number;
}

export type PhaseAction = 'edit' | 'delete' | 'details';
export type StageAction = 'edit' | 'toggle-hidden' | 'delete' | 'export' | 'details';

/**
 * Estado FatorH da vaga p/ o badge do card do módulo (spec 068/AA-48):
 * 'integracao' = integração quebrada (workspace/chave/exame) — vermelho;
 * 'pendentes' = N candidatos na fase ainda não enviados — âmbar.
 */
export type FatorHCardStatus = { kind: 'integracao' } | { kind: 'pendentes'; count: number };

export interface JobFlowPageProps {
  /** Estado FatorH p/ o badge no card do módulo (null = sem badge). */
  fatorHStatus?: FatorHCardStatus | null;
  jobName: string;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  phases: JobPhase[];
  /**
   * EU-569 — vaga Encerrada / Cancelada: kanban em modo somente visualização.
   * Cards e ações (Editar, Adicionar fase/etapa, ocultar, excluir, drag)
   * desabilitados; o botão de ação principal exibe "Edição bloqueada".
   * "Detalhes" e o export de respostas (leitura) permanecem habilitados.
   */
  readOnly?: boolean;
  /**
   * EU-675 — `true` enquanto o save (`PUT /jobs/:id/steps`) está em andamento.
   * Desabilita "Adicionar fase"/"Adicionar etapa" para o consultor não disparar
   * a criação repetida durante o envio (o guard de in-flight do container já
   * impede o PUT concorrente; isto é o reflexo visual).
   */
  isSaving?: boolean;
  onAddPhase: () => void;
  onPhaseAction: (action: PhaseAction, phaseId: string) => void;
  onAddStage: (phaseId: string) => void;
  onStageAction: (action: StageAction, phaseId: string, stageId: string) => void;
  onBack: () => void;
  /**
   * Reorder das fases via drag-and-drop horizontal (EU-165 RN-02, US-18).
   * `from` e `to` são ids estáveis das fases; container computa a nova ordem
   * (1-based `position`) e dispara o auto-save consolidado.
   */
  onReorder: (fromId: string, toId: string) => void;
  /**
   * SEL-013/SEL-014 — drag-and-drop de etapas (stages) dentro de uma fase ou
   * entre fases. `toIndex` é a posição final na fase de destino (0-based).
   * Drop dentro da mesma fase = reorder; drop em outra fase = move. Etapas
   * com `answerCount > 0` são gateadas via `useSortable({disabled})` no
   * `SortableStageCard`, então essa callback não é chamada pra elas.
   */
  onStageDrop: (stageId: string, fromPhaseId: string, toPhaseId: string, toIndex: number) => void;
  isSheetOpen: boolean;
  onSheetOpenChange: (open: boolean) => void;
  sheetValues: PhaseSheetProps['values'];
  sheetErrors: PhaseSheetProps['errors'];
  sheetIsSubmitting: PhaseSheetProps['isSubmitting'];
  sheetFormOptions: SheetSelectOption[];
  sheetTestOptions: SheetSelectOption[];
  /** RN-17 — três estados do dropdown de teste: carregando, erro, vazio. */
  sheetTestsLoading?: boolean;
  sheetTestsError?: boolean;
  sheetFatorHOptions: SheetSelectOption[];
  /** EU-539 — instâncias Kolab + estado do catálogo (loading/erro/pronto). */
  sheetKolabOptions: SheetSelectOption[];
  sheetKolabCatalogueState: 'loading' | 'error' | 'ready';
  onSheetFieldChange: PhaseSheetProps['onFieldChange'];
  onSheetContentFileChange: PhaseSheetProps['onContentFileChange'];
  onSheetSubmit: PhaseSheetProps['onSubmit'];
  sheetMode?: PhaseSheetMode;
  isAddPhaseDialogOpen: boolean;
  onAddPhaseDialogOpenChange: (open: boolean) => void;
  addPhaseValues: PhaseAddDialogProps['values'];
  addPhaseErrors: PhaseAddDialogProps['errors'];
  addPhaseIsSubmitting: PhaseAddDialogProps['isSubmitting'];
  addPhaseTypeOptions: PhaseAddDialogProps['typeOptions'];
  onAddPhaseFieldChange: PhaseAddDialogProps['onFieldChange'];
  onAddPhaseSubmit: PhaseAddDialogProps['onSubmit'];
  addPhaseMode?: PhaseDialogMode;
  /**
   * EU-165 RN-07 — a exclusão de uma fase passa por um modal de confirmação
   * dedicado; a remoção só acontece após confirmação explícita. O container
   * abre o modal (não exclui na hora); a exclusão efetiva é disparada por
   * `onConfirmDeletePhase`. A descrição do modal é estática (Figma
   * 17587-6192) — não usa mais o nome da fase.
   */
  isDeletePhaseDialogOpen: boolean;
  onDeletePhaseDialogOpenChange: (open: boolean) => void;
  onConfirmDeletePhase: () => void;
  /**
   * RN-11 — ocultar e excluir uma ETAPA passam por um modal de confirmação
   * dedicado. O container abre o modal (não age na hora); a ação efetiva é
   * disparada por `onConfirmStageAction`. `stageConfirmAction` escolhe os
   * textos do modal (`delete` → excluir, `hide` → ocultar). Reexibir uma
   * etapa oculta é imediato e não passa por aqui.
   */
  isStageConfirmOpen: boolean;
  onStageConfirmOpenChange: (open: boolean) => void;
  stageConfirmAction: 'delete' | 'hide' | null;
  stageConfirmStageName: string;
  onConfirmStageAction: () => void;
  /**
   * RN-06/RN-07 — editar uma etapa que já tem candidatos que a concluíram
   * abre o modal "Reabrir etapa" antes de a alteração ser persistida.
   * `onReopenDecision(true)` = Sim, `onReopenDecision(false)` = Não.
   */
  isReopenDialogOpen: boolean;
  onReopenDialogOpenChange: (open: boolean) => void;
  onReopenDecision: (reopen: boolean) => void;
  /** RN-13/RN-14 — trava do campo "Será possível reenviar a resposta?". */
  sheetResubmitLock: ResubmitLock;
  /** Etapa editada já tem respostas → trava Tipo + catálogo (Formulário/Teste/Fator H). */
  sheetHasAnswers: boolean;
}

function formatStageDate(iso: string | null): string {
  if (!iso) return '—';
  const [yyyy, mm, dd] = iso.split('-');
  if (!yyyy || !mm || !dd) return iso;
  return `${dd}/${mm}/${yyyy.slice(2)}`;
}

const STAGE_BADGE_STYLES: Record<StageBadgeType, { className: string; label: string }> = {
  conteudo: {
    className: 'bg-stage-conteudo text-foreground',
    label: 'Conteúdo',
  },
  form: {
    className: 'bg-stage-formulario text-primary-foreground',
    label: 'Formulário',
  },
  'fator-h': {
    className: 'bg-stage-fator-h text-foreground',
    label: 'Fator H',
  },
  case: {
    className: 'bg-stage-case text-primary-foreground',
    label: 'Case',
  },
  teste: {
    className: 'bg-stage-teste text-primary-foreground',
    label: 'Teste',
  },
  // Reaproveita o estilo do badge de Teste (sem novo token de cor).
  'teste-externo': {
    className: 'bg-stage-teste text-primary-foreground',
    label: 'Teste externo',
  },
  curriculo: {
    className: 'bg-stage-curriculo text-primary-foreground',
    label: 'Currículo',
  },
  // Reaproveita o estilo do badge de Conteúdo (sem novo token de cor).
  'complementacao-perfil': {
    className: 'bg-stage-conteudo text-foreground',
    label: 'Complementação de perfil',
  },
};

export function JobFlowPage({
  fatorHStatus = null,
  jobName,
  companyLogoUrl,
  companyName,
  phases,
  readOnly = false,
  isSaving = false,
  onAddPhase,
  onPhaseAction,
  onAddStage,
  onStageAction,
  onBack,
  onReorder,
  onStageDrop,
  isSheetOpen,
  onSheetOpenChange,
  sheetValues,
  sheetErrors,
  sheetIsSubmitting,
  sheetFormOptions,
  sheetTestOptions,
  sheetTestsLoading = false,
  sheetTestsError = false,
  sheetFatorHOptions,
  sheetKolabOptions,
  sheetKolabCatalogueState,
  onSheetFieldChange,
  onSheetContentFileChange,
  onSheetSubmit,
  sheetMode = 'add',
  sheetHasAnswers,
  isAddPhaseDialogOpen,
  onAddPhaseDialogOpenChange,
  addPhaseValues,
  addPhaseErrors,
  addPhaseIsSubmitting,
  addPhaseTypeOptions,
  onAddPhaseFieldChange,
  onAddPhaseSubmit,
  addPhaseMode = 'add',
  isDeletePhaseDialogOpen,
  onDeletePhaseDialogOpenChange,
  onConfirmDeletePhase,
  isStageConfirmOpen,
  onStageConfirmOpenChange,
  stageConfirmAction,
  stageConfirmStageName,
  onConfirmStageAction,
  isReopenDialogOpen,
  onReopenDialogOpenChange,
  onReopenDecision,
  sheetResubmitLock,
}: JobFlowPageProps) {
  const isEmpty = phases.length === 0;

  // Sensores do `@dnd-kit`: ponteiro (mouse/touch) + teclado (Tab+Space+setas
  // — acessibilidade OOTB). `activationConstraint.distance: 5` evita que um
  // click acidental no card vire um drag fantasma; o consultor precisa
  // mover o cursor 5px para ativar o drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // SEL-013 — id da etapa sendo arrastada, pra renderizar a clone visual em
  // `DragOverlay` (segue o cursor enquanto o card original permanece no
  // lugar). `null` quando o drag é de fase, ou quando nenhum drag ativo.
  const [activeDragStageId, setActiveDragStageId] = useState<string | null>(null);

  function isStageId(id: string | number): boolean {
    return phases.some((p) => p.stages.some((s) => s.id === id));
  }

  function findStageById(id: string): JobStage | undefined {
    for (const p of phases) {
      const s = p.stages.find((st) => st.id === id);
      if (s) return s;
    }
    return undefined;
  }

  function findPhaseByStageId(stageId: string): JobPhase | undefined {
    return phases.find((p) => p.stages.some((s) => s.id === stageId));
  }

  function handleDragStart(event: DragStartEvent) {
    const id = String(event.active.id);
    setActiveDragStageId(isStageId(id) ? id : null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragStageId(null);
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // SEL-013 — drag de etapa: cobre reorder dentro da mesma fase E move
    // entre fases. O drop pode cair em outra etapa (`overId` é stage) ou
    // numa fase vazia (`overId` é phase). Resolve a fase destino + index.
    if (isStageId(activeId)) {
      const fromPhase = findPhaseByStageId(activeId);
      if (!fromPhase) return;

      const overIsStage = isStageId(overId);
      const toPhase = overIsStage
        ? findPhaseByStageId(overId)
        : phases.find((p) => p.id === overId);
      if (!toPhase) return;

      let toIndex = overIsStage
        ? toPhase.stages.findIndex((s) => s.id === overId)
        : toPhase.stages.length;
      if (toIndex < 0) toIndex = toPhase.stages.length;

      onStageDrop(activeId, fromPhase.id, toPhase.id, toIndex);
      return;
    }

    // Fase: comportamento legado (US-18).
    onReorder(activeId, overId);
  }

  const activeDragStage = activeDragStageId ? findStageById(activeDragStageId) : undefined;

  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="flex flex-1 flex-col gap-8 bg-card px-4 pt-8 md:px-8">
        <header className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-2">
            <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
            <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
              {jobName}
            </h1>
          </div>
          <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
            Etapas de seleção
          </p>
        </header>

        <div className="overflow-x-auto pb-2">
          {isEmpty ? (
            <div className="flex w-full max-w-[305px]">
              <button
                type="button"
                onClick={onAddPhase}
                disabled={readOnly || isSaving}
                className="flex w-full flex-row items-center justify-center gap-2 rounded-2xl border border-border bg-card p-5 text-sm font-medium text-foreground shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)] transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
                data-testid="job-flow-add-phase"
              >
                Adicionar fase
                <PlusIcon className="size-4" />
              </button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              // SEL-013 — `closestCorners` é o padrão recomendado pelo
              // @dnd-kit pro pattern de múltiplos containers (cada fase é um
              // container de etapas). `closestCenter` é OK pra reorder linear
              // mas fica errático cross-container.
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={phases.map((p) => p.id)}
                strategy={horizontalListSortingStrategy}
              >
                <ol className="flex flex-row items-stretch gap-5">
                  {phases.map((phase) => (
                    <SortablePhaseItem
                      key={phase.id}
                      phase={phase}
                      readOnly={readOnly}
                      isSaving={isSaving}
                      fatorHStatus={fatorHStatus}
                      onPhaseAction={(action) => onPhaseAction(action, phase.id)}
                      onStageAction={(action, stageId) => onStageAction(action, phase.id, stageId)}
                      onAddStage={() => onAddStage(phase.id)}
                    />
                  ))}

                  <li className="flex w-[305px] shrink-0">
                    <button
                      type="button"
                      onClick={onAddPhase}
                      disabled={readOnly || isSaving}
                      className="flex w-full flex-row items-center justify-center gap-2 self-start rounded-2xl border border-border bg-card p-5 text-sm font-medium text-foreground shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)] transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
                      data-testid="job-flow-add-phase"
                    >
                      Adicionar fase
                      <PlusIcon className="size-4" />
                    </button>
                  </li>
                </ol>
              </SortableContext>
              {/* SEL-013 — clone da etapa arrastada que segue o cursor. Sem
                  o overlay, o card original some durante o drag (mantido
                  por @dnd-kit) e o consultor não enxerga o que está
                  movendo. `onAction` no-op porque é só visual. */}
              <DragOverlay>
                {activeDragStage ? (
                  <StageCard stage={activeDragStage} onAction={() => undefined} />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>

        {/* RN-09 — rodapé flutuante fixado na base da tela durante a rolagem:
            "Voltar" + indicador "Salvamento automático". Não há botão Salvar —
            as alterações são persistidas automaticamente; o botão de
            salvamento fica desabilitado, presente apenas como indicador
            visual. As margens negativas sangram a borda superior até as
            extremidades do `<main>`. */}
        <div className="sticky bottom-0 -mx-4 mt-auto flex flex-row items-center justify-between gap-2 border-t border-border bg-card px-4 py-4 md:-mx-8 md:px-8">
          <Button
            variant="outline"
            size="lg"
            onClick={onBack}
            type="button"
            data-testid="job-flow-back"
          >
            Voltar
          </Button>
          {readOnly ? (
            <Button
              size="lg"
              type="button"
              disabled
              aria-disabled="true"
              data-testid="job-flow-edit-blocked"
            >
              Edição bloqueada
            </Button>
          ) : (
            <Button
              size="lg"
              type="button"
              disabled
              aria-disabled="true"
              data-testid="job-flow-autosave-indicator"
            >
              Salvamento automático
            </Button>
          )}
        </div>
      </main>

      <PhaseSheet
        open={isSheetOpen}
        onOpenChange={onSheetOpenChange}
        values={sheetValues}
        errors={sheetErrors}
        isSubmitting={sheetIsSubmitting}
        formOptions={sheetFormOptions}
        testOptions={sheetTestOptions}
        testsLoading={sheetTestsLoading}
        testsError={sheetTestsError}
        fatorHOptions={sheetFatorHOptions}
        kolabOptions={sheetKolabOptions}
        kolabCatalogueState={sheetKolabCatalogueState}
        onFieldChange={onSheetFieldChange}
        onContentFileChange={onSheetContentFileChange}
        onSubmit={onSheetSubmit}
        mode={sheetMode}
        resubmitLock={sheetResubmitLock}
        hasAnswers={sheetHasAnswers}
      />

      <PhaseAddDialog
        open={isAddPhaseDialogOpen}
        onOpenChange={onAddPhaseDialogOpenChange}
        values={addPhaseValues}
        errors={addPhaseErrors}
        isSubmitting={addPhaseIsSubmitting}
        typeOptions={addPhaseTypeOptions}
        onFieldChange={onAddPhaseFieldChange}
        onSubmit={onAddPhaseSubmit}
        mode={addPhaseMode}
      />

      {/* EU-165 RN-07 — modal de confirmação dedicado da exclusão de fase.
          Reaproveita o `JobConfirmDialog` (mesmo componente usado pelo
          hub de edição de vagas e pela listagem) para manter a UX de
          confirmação destrutiva consistente em toda a feature de vagas.
          Textos seguem o Figma (node 17587-6192). */}
      <JobConfirmDialog
        open={isDeletePhaseDialogOpen}
        onOpenChange={onDeletePhaseDialogOpenChange}
        title="Tem certeza?"
        description="Ao continuar, esta fase e todas suas etapas serão excluídas permanentemente. Esta ação não pode ser desfeita."
        confirmLabel="Excluir fase"
        onConfirm={onConfirmDeletePhase}
      />

      {/* RN-11 — modal de confirmação dedicado para ocultar / excluir uma
          ETAPA. Reaproveita o `JobConfirmDialog`; `stageConfirmAction`
          escolhe os textos. A ação só é aplicada após confirmar. O ramo de
          exclusão segue o Figma (node 17587-6170); o de ocultar não tem
          frame de Figma e mantém a cópia descritiva existente. */}
      <JobConfirmDialog
        open={isStageConfirmOpen}
        onOpenChange={onStageConfirmOpenChange}
        title={stageConfirmAction === 'delete' ? 'Tem certeza?' : 'Ocultar etapa?'}
        description={
          stageConfirmAction === 'delete'
            ? 'Ao continuar, esta etapa será excluída permanentemente. Esta ação não pode ser desfeita.'
            : `A etapa "${stageConfirmStageName}" deixará de aparecer para os candidatos. Você ainda poderá vê-la e reexibi-la quando quiser.`
        }
        confirmLabel={stageConfirmAction === 'delete' ? 'Excluir etapa' : 'Ocultar'}
        onConfirm={onConfirmStageAction}
      />

      {/* RN-06/RN-07 — modal "Reabrir etapa", disparado ao salvar a edição de
          uma etapa que já tem candidatos que a concluíram. */}
      <StageReopenDialog
        open={isReopenDialogOpen}
        onOpenChange={onReopenDialogOpenChange}
        onDecision={onReopenDecision}
      />
    </div>
  );
}

/**
 * Wrapper sortable de cada fase. `useSortable` da fase fica registrado no
 * `<li>` (pra DnD do @dnd-kit ler `transform`), mas os `listeners` do drag
 * são propagados pro `PhaseHeader` — só o cabeçalho atua como handle. Sem
 * isso, agarrar uma etapa também dispararia o drag da fase (bubble).
 *
 * SortableContext interno (vertical) habilita reorder/move das etapas. As
 * etapas com `answerCount > 0` ficam `disabled` no `useSortable` do stage
 * (SEL-014) — o pointer não engata e o consultor recebe `cursor-not-allowed`.
 */
interface SortablePhaseItemProps {
  phase: JobPhase;
  fatorHStatus?: FatorHCardStatus | null;
  /** EU-569 — modo visualização: trava drag, menu, ações e "Adicionar etapa". */
  readOnly?: boolean;
  /** EU-675 — desabilita "Adicionar etapa" enquanto o save está em andamento. */
  isSaving?: boolean;
  onPhaseAction: (action: PhaseAction) => void;
  onStageAction: (action: StageAction, stageId: string) => void;
  onAddStage: () => void;
}

function SortablePhaseItem({
  phase,
  fatorHStatus = null,
  readOnly = false,
  isSaving = false,
  onPhaseAction,
  onStageAction,
  onAddStage,
}: SortablePhaseItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: phase.id,
    disabled: readOnly,
  });
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      // SEL-018 — coluna com altura máxima relativa ao viewport. Header e
      // botão "Adicionar etapa" ficam fora do scroll (sempre visíveis); só a
      // lista de etapas rola quando ultrapassa a altura. Os 220px cobrem:
      // header do consultor (~120px) + rodapé sticky (~80px) + margens. Em
      // viewports menores o `max-h` evita que a coluna empurre a página.
      className="flex max-h-[calc(100dvh-220px)] w-[305px] shrink-0 flex-col gap-5"
      data-testid={`job-flow-phase-item-${phase.id}`}
    >
      <PhaseHeader
        phase={phase}
        readOnly={readOnly}
        onAction={onPhaseAction}
        dragAttributes={readOnly ? undefined : attributes}
        dragListeners={readOnly ? undefined : listeners}
      />
      <SortableContext items={phase.stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        <div className="-mr-1 flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pr-1">
          {phase.stages.map((stage) => (
            <SortableStageCard
              key={stage.id}
              stage={stage}
              fatorHStatus={stage.type === 'fator-h' ? fatorHStatus : null}
              readOnly={readOnly}
              onAction={(action) => onStageAction(action, stage.id)}
            />
          ))}
        </div>
      </SortableContext>
      <Button
        variant="outline"
        size="sm"
        onClick={onAddStage}
        disabled={readOnly || isSaving}
        data-testid={`job-flow-add-stage-${phase.id}`}
      >
        Adicionar etapa
      </Button>
    </li>
  );
}

interface SortableStageCardProps {
  stage: JobStage;
  fatorHStatus?: FatorHCardStatus | null;
  /** EU-569 — modo visualização: trava drag e ações do card. */
  readOnly?: boolean;
  onAction: (action: StageAction) => void;
}

/**
 * Wrapper sortable de cada etapa. O card todo é handle de arrasto (sem ícone
 * dedicado, igual fase). SEL-014: etapas com `answerCount > 0` ficam
 * `disabled` — o pointer não engata o drag e o consultor vê `cursor-not-allowed`.
 */
function SortableStageCard({
  stage,
  fatorHStatus = null,
  readOnly = false,
  onAction,
}: SortableStageCardProps) {
  const draggable = (stage.answerCount ?? 0) === 0 && !readOnly;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
    disabled: !draggable,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className={cn(draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-not-allowed')}
      {...attributes}
      {...listeners}
    >
      <StageCard
        stage={stage}
        fatorHStatus={fatorHStatus}
        readOnly={readOnly}
        onAction={onAction}
      />
    </div>
  );
}

interface PhaseHeaderProps {
  phase: JobPhase;
  /** EU-569 — modo visualização: menu de ações da fase desabilitado. */
  readOnly?: boolean;
  onAction: (action: PhaseAction) => void;
  /**
   * Listeners do `useSortable` da fase — quando presentes, o cabeçalho atua
   * como drag-handle da fase. Quando ausentes, o header é estático (usado
   * pelo `DragOverlay`, por exemplo). Movido pra cá porque o `<li>` inteiro
   * conflitava com o drag das etapas (event bubble).
   */
  dragAttributes?: DraggableAttributes;
  dragListeners?: DraggableSyntheticListeners;
}

function PhaseHeader({
  phase,
  readOnly = false,
  onAction,
  dragAttributes,
  dragListeners,
}: PhaseHeaderProps) {
  const hasDates = phase.startDate !== null || phase.endDate !== null;
  return (
    <div
      className={cn(
        'flex flex-row items-start justify-between gap-2 rounded-lg bg-muted p-5 shadow-[0_1px_2px_0_rgba(33,41,40,0.05)]',
        dragListeners && 'cursor-grab active:cursor-grabbing'
      )}
      {...dragAttributes}
      {...dragListeners}
    >
      <div className="flex flex-1 flex-col gap-1">
        <span className="text-base font-medium leading-none text-foreground">{phase.name}</span>
        {/* EU-165 FR-045/046 — card exibe "Começa" e "Encerra" derivados das
            datas da fase. Quando ambas são null, esconde a linha (mantém o
            visual limpo igual ao Figma "Etapas de seleção" empty). */}
        {hasDates ? (
          <div className="flex flex-col gap-0.5 text-xs leading-none text-muted-foreground">
            <div className="flex flex-row gap-1">
              <span>Começa:</span>
              <span className="font-medium">{formatStageDate(phase.startDate)}</span>
            </div>
            <div className="flex flex-row gap-1">
              <span>Encerra:</span>
              <span className="font-medium">{formatStageDate(phase.endDate)}</span>
              {phase.endTime ? (
                <>
                  <span className="ml-1">às</span>
                  <span className="font-medium">{phase.endTime}</span>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Ações para ${phase.name}`}
            disabled={readOnly}
            className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 data-[state=open]:bg-card data-[state=open]:text-foreground disabled:opacity-40"
            data-testid={`job-flow-phase-menu-${phase.id}`}
          >
            <EllipsisVerticalIcon className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[200px]">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => onAction('details')}>Detalhes</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction('edit')}>Editar</DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem destructive onClick={() => onAction('delete')}>
              Excluir
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

interface StageCardProps {
  stage: JobStage;
  /** Só chega preenchido em card de módulo FatorH (spec 068). */
  fatorHStatus?: FatorHCardStatus | null;
  /** EU-569 — modo visualização: Editar/ocultar/excluir desabilitados. */
  readOnly?: boolean;
  onAction: (action: StageAction) => void;
}

function StageCard({ stage, fatorHStatus = null, readOnly = false, onAction }: StageCardProps) {
  const badge = STAGE_BADGE_STYLES[stage.type];
  const muted = stage.hidden;
  // RN-16 — reexibir é o que a regra proíbe; ocultar continua livre (serve
  // para o consultor corrigir uma etapa que ficou visível indevidamente).
  const unhideBlocked = Boolean(stage.hidden && stage.hiddenLocked);
  // RN-10 — etapa com respostas não pode ser excluída, só ocultada. O gate
  // comportamental (toast + redirect para hide) já existe em
  // `handleStageAction('delete')`, mas o ícone também precisa refletir o
  // estado: `disabled` + `aria-disabled` + cursor + opacidade para o
  // consultor saber visualmente que não tem como deletar.
  const deleteDisabled = (stage.answerCount ?? 0) > 0 || readOnly;

  return (
    <article className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-5 shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)]">
      <div className={cn('flex flex-col gap-2', muted && 'opacity-50')}>
        <span
          className={cn(
            'inline-flex w-fit items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium leading-4',
            badge.className
          )}
        >
          {badge.label}
        </span>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium leading-none text-foreground">{stage.name}</p>
          <div className="flex flex-row gap-1 text-xs leading-none text-muted-foreground">
            <span>Começa:</span>
            <span className="font-medium">{formatStageDate(stage.startDate)}</span>
          </div>
          <div className="flex flex-row gap-1 text-xs leading-none text-muted-foreground">
            <span>Encerra:</span>
            <span className="font-medium">{formatStageDate(stage.endDate)}</span>
            {stage.endTime ? (
              <>
                <span className="ml-1">às</span>
                <span className="font-medium">{stage.endTime}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* RN-10 — informativo do gate de delete. Etapa com respostas só pode ser
          ocultada; sem isso o consultor não entende por que a lixeira está
          desabilitada. Fica fora do wrapper `muted` pra continuar legível mesmo
          numa etapa oculta. Mesma linguagem do toast `stage_has_answers`. */}
      {(stage.answerCount ?? 0) > 0 ? (
        <div
          className="flex items-center gap-1.5 rounded-lg bg-muted px-2 py-1 text-xs font-medium leading-4 text-muted-foreground"
          role="note"
        >
          <LockIcon className="size-3.5 shrink-0" aria-hidden="true" />
          <span>Com respostas — só pode ser ocultada</span>
        </div>
      ) : null}

      {/* Spec 068 (AA-48) — o envio FatorH nunca mais falha em silêncio: o card
          acusa integração quebrada (vermelho) ou pendências de envio (âmbar).
          Clique no ⓘ abre o painel com a remediação/envio. */}
      {fatorHStatus ? (
        <button
          type="button"
          onClick={() => onAction('details')}
          role="note"
          className={cn(
            'flex items-center gap-1.5 rounded-lg px-2 py-1 text-left text-xs font-medium leading-4',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            fatorHStatus.kind === 'integracao'
              ? 'bg-destructive/10 text-destructive'
              : 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
          )}
        >
          <InfoIcon className="size-3.5 shrink-0" aria-hidden="true" />
          <span>
            {fatorHStatus.kind === 'integracao'
              ? 'Integração FatorH pendente — clique para ver'
              : `${fatorHStatus.count} candidato${fatorHStatus.count === 1 ? '' : 's'} a enviar ao FatorH`}
          </span>
        </button>
      ) : null}

      <div className="h-px bg-border" />

      <div className="flex flex-row items-center justify-between">
        <button
          type="button"
          onClick={() => onAction('edit')}
          disabled={readOnly}
          className="text-sm font-medium leading-none text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm disabled:opacity-40"
        >
          Editar
        </button>
        <div className="flex flex-row items-center gap-2">
          <button
            type="button"
            aria-label={`Detalhes — ${stage.name}`}
            title="Detalhes"
            onClick={() => onAction('details')}
            className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <InfoIcon className="size-4" />
          </button>
          {/* Ícone de download inline — etapas survey-backed (formulário +
              avaliação/assessment: Raio-X Comportamental, Raio-X, Fit Cultural).
              Baixa o XLSX com a base de respostas (1 candidato/linha, 1 pergunta/
              coluna). Detectadas pelo catálogo VINCULADO (`formId`/`testId`),
              não pelo token de tipo — o BFF rebaixa para `form` os tipos que
              não mapeia e o ícone aparecia em etapas sem base de respostas.
              Ver `canExportStageResponses`. */}
          {canExportStageResponses(stage) ? (
            <button
              type="button"
              aria-label={`Exportar base de respostas — ${stage.name}`}
              title="Exportar base de respostas"
              onClick={() => onAction('export')}
              className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              data-testid={`job-flow-stage-export-${stage.id}`}
            >
              <DownloadIcon className="size-4" />
            </button>
          ) : null}
          <button
            type="button"
            aria-label={
              unhideBlocked
                ? `Mostrar ${stage.name} indisponível — não disponível para vagas de aprendiz`
                : muted
                  ? `Mostrar ${stage.name}`
                  : `Ocultar ${stage.name}`
            }
            title={unhideBlocked ? 'Não disponível para vagas de aprendiz' : undefined}
            onClick={() => onAction('toggle-hidden')}
            disabled={readOnly || unhideBlocked}
            aria-disabled={unhideBlocked || undefined}
            data-testid={`job-flow-stage-toggle-hidden-${stage.id}`}
            className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-40"
          >
            {muted ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
          </button>
          <button
            type="button"
            aria-label={
              (stage.answerCount ?? 0) > 0
                ? `Excluir ${stage.name} indisponível — etapa com respostas`
                : `Excluir ${stage.name}`
            }
            aria-disabled={deleteDisabled || undefined}
            disabled={deleteDisabled}
            onClick={() => onAction('delete')}
            className={cn(
              'inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              deleteDisabled &&
                'pointer-events-none cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground'
            )}
          >
            <TrashIcon className="size-4" />
          </button>
        </div>
      </div>
    </article>
  );
}
